import * as THREE from "three";
import { OrbitControls } from "three/orbitcontrols";

function initPodcastScene() {
  const canvas = document.querySelector("#episode-hero-ring-canvas");
  if (!canvas) return;

  const container = canvas.parentElement;
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x000000);

  const width = container.clientWidth || window.innerWidth;
  const height = container.clientHeight || window.innerHeight;

  // ----- CAMERA -----
  // Adjust FOV and position to ensure rings are fully visible
  const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
  // Position camera to frame all rings properly
  camera.position.set(0, 0, 22);

  // ----- RENDERER -----
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: false,
  });
  renderer.setSize(width, height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputEncoding = THREE.sRGBEncoding;

  // Set canvas background color immediately (like earth-3d.js effect.domElement)
  canvas.style.backgroundColor = "#000000";

  // Render immediately to show black background
  renderer.render(scene, camera);

  // ----- CONTROLS -----
  const controls = new OrbitControls(camera, renderer.domElement);
  Object.assign(controls, {
    enableDamping: true,
    dampingFactor: 0.05,
    enableZoom: false,
    enablePan: false,
    autoRotate: false,
    enableRotate: false,
  });

  // ----- LIGHTING -----
  scene.add(new THREE.AmbientLight(0xffffff, 0.9));

  // ----- RINGS (SOUND WAVES) -----
  const waves = [
    createSoundWave(3.2), // Innermost ring radius
    createSoundWave(4.4),
    createSoundWave(3.4),
  ];

  const ringGroup = new THREE.Group();

  waves.forEach((w) => {
    w.scale.set(0.85, 0.85, 0.85);
    w.material.transparent = true;
    w.material.opacity = 0.2;
    ringGroup.add(w);
  });

  // ----- SCALING -----
  // Scale adjusted to ensure rings fit in viewport
  const baseScale = 4.0;
  const refWidth = 1008;
  if (window.innerWidth <= 1200) {
    const scaleFactor = Math.max(1.6, (width / refWidth) * baseScale);
    ringGroup.scale.set(scaleFactor, scaleFactor, scaleFactor);
  } else {
    ringGroup.scale.set(baseScale, baseScale, baseScale);
  }

  // Position rings centered in viewport
  ringGroup.position.set(0, 0, 0);

  scene.add(ringGroup);

  // ----- INITIAL RENDER -----
  // Render immediately after adding objects
  renderer.render(scene, camera);

  waves.forEach((wave, i) => {
    const baseScale = 1 + i * 0.1;

    // Initial load animations for all circles
    gsap.to(wave.material, {
      opacity: 1,
      duration: 2,
      ease: "power2.out",
      delay: 0.5,
    });

    // Infinite damping ripple animation with smooth continuous loop
    const initialAmplitude = 0.015; // Starting amplitude
    const minAmplitude = 0.002; // Minimum amplitude before reset
    const dampingFactor = 0.85; // Each cycle reduces amplitude by 15% (slower damping)
    const baseDuration = 1.2; // Base duration for each expand/contract cycle

    // Create infinite loop animation with smooth damping
    const scaleTl = gsap.timeline({
      delay: 0.5,
      repeat: -1, // Infinite repeat
    });

    // Function to create a single ripple cycle with given amplitude
    const createRippleCycle = (amplitude, duration) => {
      // Expand phase
      scaleTl.to(wave.scale, {
        x: baseScale * (1 + amplitude),
        y: baseScale * (1 + amplitude),
        z: baseScale * (1 + amplitude),
        duration: duration,
        ease: "sine.out",
      });

      // Contract phase
      scaleTl.to(wave.scale, {
        x: baseScale * (1 - amplitude * 0.75),
        y: baseScale * (1 - amplitude * 0.75),
        z: baseScale * (1 - amplitude * 0.75),
        duration: duration * 0.75,
        ease: "sine.in",
      });

      // Return to base scale
      scaleTl.to(wave.scale, {
        x: baseScale,
        y: baseScale,
        z: baseScale,
        duration: duration * 0.5,
        ease: "sine.inOut",
      });
    };

    // Create damping cycles: amplitude decreases gradually
    let currentAmplitude = initialAmplitude;
    let currentDuration = baseDuration;

    // Damping phase: amplitude decreases
    while (currentAmplitude > minAmplitude) {
      createRippleCycle(currentAmplitude, currentDuration);
      currentAmplitude *= dampingFactor;
      currentDuration *= 0.98; // Slight duration adjustment
    }

    // Smooth reset phase: gradually increase amplitude back
    const resetCycles = 5;
    const resetAmplitudeStep = (initialAmplitude - minAmplitude) / resetCycles;

    for (let i = 1; i <= resetCycles; i++) {
      currentAmplitude = minAmplitude + resetAmplitudeStep * i;
      currentDuration = baseDuration * (0.95 + (0.05 * i) / resetCycles); // Gradually return duration
      createRippleCycle(currentAmplitude, currentDuration);
    }
  });

  // ----- RENDER LOOP -----
  function animate() {
    requestAnimationFrame(animate);
    controls.update();
    renderer.render(scene, camera);
  }

  // ----- RESIZE -----
  function onResize() {
    const newW = container.clientWidth || window.innerWidth;
    const newH = container.clientHeight || window.innerHeight;

    renderer.setSize(newW, newH);
    camera.aspect = newW / newH;
    camera.updateProjectionMatrix();
    if (window.innerWidth <= 1200) {
      const newScale = Math.max(1.5, (newW / refWidth) * baseScale);
      ringGroup.scale.set(newScale, newScale, newScale);
    } else {
      ringGroup.scale.set(baseScale, baseScale, baseScale);
    }
  }

  window.addEventListener("resize", onResize);
  animate();

  // ----- CLEANUP -----
  return () => {
    window.removeEventListener("resize", onResize);
    gsap.globalTimeline.clear();
    controls.dispose();
    ringGroup.traverse((obj) => {
      if (obj.isMesh) {
        obj.geometry.dispose();
        obj.material.dispose();
      }
    });
    renderer.dispose();
  };
}

// ----- CREATE SOUND WAVE -----
function createSoundWave(
  radius = 1.5,
  color = 0x00ffff,
  start = -0.7,
  end = Math.PI * 1.2,
) {
  const curve = new THREE.ArcCurve(0, 0, radius, start, end, false);
  const points = curve.getPoints(100);
  const geometry = new THREE.BufferGeometry().setFromPoints(points);

  const lineDistances = [0];
  for (let i = 1; i < points.length; i++) {
    lineDistances.push(
      lineDistances[i - 1] + points[i].distanceTo(points[i - 1]),
    );
  }
  geometry.setAttribute(
    "lineDistance",
    new THREE.Float32BufferAttribute(lineDistances, 1),
  );

  const gradient = [
    0xffc0cb, 0xffb6d9, 0xb8c0ff, 0x87ceeb, 0x5da3ff, 0x008b8b, 0x2e8b57,
    0x2e8b57, 0xffeb99, 0xffd4a3, 0xffb6a3,
  ].map((c) => new THREE.Color(c));

  const colors = [];
  const minY = Math.min(...points.map((p) => p.y));
  const maxY = Math.max(...points.map((p) => p.y));

  for (let i = 0; i < points.length; i++) {
    const t = i / (points.length - 1);
    const idx = Math.floor(t * (gradient.length - 1));
    const next = Math.min(idx + 1, gradient.length - 1);
    const color = new THREE.Color().lerpColors(
      gradient[idx],
      gradient[next],
      t * (gradient.length - 1) - idx,
    );

    const yNorm = (points[i].y - minY) / (maxY - minY);
    const opacity = 0.2 + yNorm * 0.8;
    colors.push(color.r, color.g, color.b, opacity);
  }

  geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 4));

  const material = new THREE.LineDashedMaterial({
    vertexColors: true,
    transparent: true,
    linewidth: 2,
    dashSize: 0.02,
    gapSize: 0.02,
  });

  const line = new THREE.Line(geometry, material);
  line.position.y = -0.3;
  return line;
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initPodcastScene);
} else {
  initPodcastScene();
}

export { initPodcastScene };
